// FIXME
// eslint-disable-next-line workspaces/require-dependency
import { type DefaultRootState } from '@databases/redux/default-root-state';
import {
  type BasicDatabase,
  type BrokenDatabase,
  type Database,
  type NumberFormat,
  type ShallowDatabase
} from '@databases/types/database';
import { createCachedSelector } from 're-reselect';

import {
  isBasicDatabase,
  isBrokenDatabase,
  isNonShallowDatabase,
  isNonShallowOrBrokenDatabase,
  isNonBasicDatabase,
  isNonBrokenDatabase,
  getDatabaseFieldRequired
} from '../../database/predicates.ts';
import { deserializeError } from '../../utils.ts';
import {
  type AsyncOperation,
  AsyncOperationStatus,
  validateStatus
} from '../async-operation.ts';

import { getIdKey } from './slice.ts';
import { isVirtualContentId } from './utils.ts';

/**
 * Returns all databases (also broken ones) in the given space that are currently persisted in the store.
 */
export const selectSpaceDatabases = createCachedSelector<
  DefaultRootState,
  { spaceKey: string },
  Record<string, Database | ShallowDatabase | BasicDatabase | BrokenDatabase>,
  string,
  Array<ShallowDatabase | BrokenDatabase>
>(
  (state) => state.databases.databases,
  (state, { spaceKey }) => spaceKey,
  (databasesByContentId, spaceKey) => {
    return Object.values(databasesByContentId)
      .filter(isNonBasicDatabase)
      .filter((db) => db.space.key === spaceKey && !isVirtualContentId(db.contentId));
  }
)((state, { spaceKey }) => spaceKey);

/**
 * Returns the load status of the loadSpaceDatabases action for the given space.
 */
export function selectSpaceDatabasesStatus(
  state: DefaultRootState,
  { spaceKey }: { spaceKey: string }
): AsyncOperation<void> | undefined {
  return state.databases.spaceDatabasesStatus[spaceKey];
}

/**
 * Returns the database with the specified content ID from the store. If the database does not exist or an error occurred
 * while loading it, an error is thrown.
 * @throws If the database does not exist or an error occurred while loading it, an error is thrown.
 */
export function selectDatabaseByContentIdRequired(
  state: DefaultRootState,
  { contentId }: { contentId: string }
): Database {
  validateStatus(selectDatabaseByContentIdStatus(state, { contentId }));

  const database = selectDatabaseByContentIdOptional(state, { contentId });
  if (!database) {
    throw new Error(`Could not find database ${contentId}.`);
  } else if (isBrokenDatabase(database)) {
    throw deserializeError(database.error);
  }
  return database;
}

/**
 * Returns the database with the specified content ID from the store. If the database does not exist, undefined is returned.
 * If there was a validation error while deserializing the database, a BrokenDatabase object is returned. If there was an
 * error loading the database, undefined is returned.
 */
export function selectDatabaseByContentIdOptional(
  state: DefaultRootState,
  { contentId }: { contentId: string }
): Database | BrokenDatabase | undefined {
  if (
    selectDatabaseByContentIdStatus(state, { contentId })?.status ===
    AsyncOperationStatus.ERROR
  ) {
    return undefined;
  }

  const database = state.databases.databases[contentId];
  return database && !isBasicDatabase(database) && isNonShallowOrBrokenDatabase(database)
    ? database
    : undefined;
}

export function selectDatabaseNameByContentIdOptional(
  state: DefaultRootState,
  { contentId }: { contentId: string }
): string | undefined {
  const database = selectDatabaseByContentIdOptional(state, { contentId });
  return database?.name;
}

export function selectShallowDatabaseByContentIdOptional(
  state: DefaultRootState,
  { contentId }: { contentId: string }
): ShallowDatabase | BrokenDatabase | undefined {
  const database = state.databases.databases[contentId];
  return !isBasicDatabase(database) ? database : undefined;
}

/**
 * Returns the database with the specified unique ID from the store. If the database does not exist or there was an error while loading
 * it, an error is thrown.
 * @throws If the database does not exist or there was an error while loading it, an error is thrown.
 */
export function selectDatabaseByIdRequired(
  state: DefaultRootState,
  { spaceKey, id }: { spaceKey: string; id: string }
): Database {
  validateStatus(selectDatabaseByIdStatus(state, { spaceKey, id }));

  const database = selectDatabaseByIdOptional(state, { spaceKey, id });
  if (!database) {
    throw new Error(`Could not find database ${id}.`);
  }
  return database;
}

export const selectDatabasesById = createCachedSelector<
  DefaultRootState,
  Record<string, Database | ShallowDatabase | BasicDatabase | BrokenDatabase>,
  Record<string, Database>
>(
  (state) => state.databases.databases,
  (databasesByContentId) => {
    return Object.fromEntries(
      Object.values(databasesByContentId)
        .filter(isNonBasicDatabase)
        .filter(isNonShallowDatabase)
        .map((db) => [getIdKey(db.space.key, db.id), db])
    );
  }
)(() => '');

export const selectFullOrShallowDatabasesById = createCachedSelector<
  DefaultRootState,
  Record<string, Database | ShallowDatabase | BasicDatabase | BrokenDatabase>,
  Record<string, Database | ShallowDatabase>
>(
  (state) => state.databases.databases,
  (databasesByContentId) => {
    return Object.fromEntries(
      Object.values(databasesByContentId)
        .filter(isNonBasicDatabase)
        .filter(isNonBrokenDatabase)
        .map((db) => [getIdKey(db.space.key, db.id), db])
    );
  }
)(() => '');

/**
 * Returns the database with the specified unique ID from the store. If the database does not exist, undefined is returned.
 * If there was an error while loading the database, undefined is returned.
 */
export function selectDatabaseByIdOptional(
  state: DefaultRootState,
  { spaceKey, id }: { spaceKey: string | undefined; id: string | undefined }
): Database | undefined {
  if (spaceKey == null || id == null) {
    return undefined;
  } else if (
    selectDatabaseByIdStatus(state, { spaceKey, id })?.status ===
    AsyncOperationStatus.ERROR
  ) {
    return undefined;
  } else {
    return selectDatabasesById(state)[getIdKey(spaceKey, id)];
  }
}

/**
 * Returns the load status of the loadDatabaseByContentId action for the given content ID.
 */
export function selectDatabaseByContentIdStatus(
  state: DefaultRootState,
  { contentId }: { contentId: string }
): AsyncOperation<void> | undefined {
  return state.databases.databaseByContentIdStatus[contentId];
}

/**
 * Returns the load status of the loadDatabaseById action for the given space key and database ID.
 */
export function selectDatabaseByIdStatus(
  state: DefaultRootState,
  { spaceKey, id }: { spaceKey: string | undefined; id: string | undefined }
): AsyncOperation<void> | undefined {
  if (spaceKey == null || id == null) {
    return undefined;
  }

  return state.databases.databaseByIdStatus[getIdKey(spaceKey, id)];
}

/**
 * Like selectDatabaseByContentIdOptional, but also returns basic databases.
 */
export function selectAnyDatabaseByContentId(
  state: DefaultRootState,
  { contentId }: { contentId: string }
): Database | ShallowDatabase | BasicDatabase | BrokenDatabase | undefined {
  return state.databases.databases[contentId];
}

/**
 * Like selectDatabaseByIdOptional, but also returns basic databases.
 */
export function selectFullOrShallowDatabasesByIdOptional(
  state: DefaultRootState,
  { spaceKey, id }: { spaceKey: string | undefined; id: string | undefined }
): Database | ShallowDatabase | undefined {
  if (spaceKey == null || id == null) {
    return undefined;
  } else if (
    selectDatabaseByIdStatus(state, { spaceKey, id })?.status ===
    AsyncOperationStatus.ERROR
  ) {
    return undefined;
  } else {
    return selectFullOrShallowDatabasesById(state)[getIdKey(spaceKey, id)];
  }
}

/**
 * Returns all non-shallow databases that are currently in the store.
 */
export const selectAllDatabases = createCachedSelector<
  DefaultRootState,
  Record<string, Database | ShallowDatabase | BasicDatabase | BrokenDatabase>,
  Array<Database>
>(
  (state) => state.databases.databases,
  (databasesByContentId) => {
    return Object.values(databasesByContentId)
      .filter(isNonBasicDatabase)
      .filter(isNonShallowDatabase)
      .filter((db) => !isVirtualContentId(db.contentId));
  }
)(() => '');

/**
 * Returns a list of content IDs of all databases currently in the store.
 */
export const selectAllDatabaseIds = createCachedSelector<
  DefaultRootState,
  Database[],
  string[]
>(
  (state) => selectAllDatabases(state),
  (databases) => {
    return databases.map((db) => db.contentId);
  }
)(() => '');

export function selectFieldNumberFormat(
  state: DefaultRootState,
  { database, fieldId }: { database: Database; fieldId: string }
): NumberFormat | undefined {
  const field = getDatabaseFieldRequired(database, fieldId);
  return field.settings.numberFormat;
}
